import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Button, Stack, styled } from '@mui/material'
import { useWeb3React } from '@web3-react/core'
import { useAtomValue } from 'jotai/index'
import { memo } from 'react'
import { Currency } from '../../api/v1/market'
import toastify from '../../components/Toastify'
import { FaucetTokenSize } from '../../constants/faucet'
import { ErrorCode } from '../../connection/utils'
import { useCurrentNetwork } from '../../store/app/hooks'
import { FaucetType, faucetTypeAtom } from './atoms'

const CoinItem = styled(Stack)`
  flex-direction: row;
  height: 48px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  margin-right: 4px;
  cursor: pointer;
  font-size: 14px;
  border: 1px solid rgba(255, 255, 255, 0);
  color: ${({ theme }) => theme.color.text100};

  &.active {
    background: ${({ theme }) => theme.color.bg};
    border: 1px solid ${({ theme }) => theme.color.primary30};
  }
`
const CoinItemLeft = styled(Stack)`
  flex: 1;
  flex-direction: row;
  align-items: center;
`
const CoinImg = styled('img')`
  width: 24px;
  height: 24px;
  margin-right: 6px;
  border-radius: 50%;
`
const CoinName = styled('div')`
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`
const TokenActions = styled(Stack)`
  flex-direction: row;
  gap: 8px;
  margin-left: 8px;
`
const TokenActionButton = styled(Button)`
  color: ${({ theme }) => theme.color.text50};
  font-size: 18px;

  &:hover {
    color: ${({ theme }) => theme.color.primary40};
  }
`

export const TokenItem = memo<{
  item: Currency
  active: boolean
  onChangeToken: (token: Currency) => void
}>(({ item, active, onChangeToken }) => {
  const faucetType = useAtomValue(faucetTypeAtom)
  const { layerTwoChainId = 0 } = useCurrentNetwork()
  const { tokenIconUrl, name, l2Symbol, id } = item
  const { address, decimals } = item?.chains[layerTwoChainId] ?? {}
  const { connector } = useWeb3React()

  const renderLayer1Actions = () => {
    if (!connector) return null
    if (!connector.watchAsset) return null
    if (faucetType !== FaucetType.Layer1) return null

    const assetParams = {
      address,
      symbol: l2Symbol,
      decimals,
      image: tokenIconUrl,
    }

    const values = Object.values(assetParams)

    if (values.some((value) => !value)) return null

    return (
      <TokenActions>
        <TokenActionButton
          title="Add token to your wallet"
          onClick={(event) => {
            event.stopPropagation()
            connector.watchAsset!(assetParams).catch((e) => {
              if (e?.code === ErrorCode.USER_REJECTED_REQUEST) {
                return
              }
              toastify.error(`Failed to add token ${e?.message}`)
            })
          }}
        >
          <AddCircleIcon fontSize="inherit" color="inherit" />
        </TokenActionButton>
      </TokenActions>
    )
  }
  return (
    <CoinItem
      key={id}
      onClick={() => onChangeToken(item)}
      className={active ? 'active' : ''}
    >
      <CoinItemLeft>
        <CoinImg src={tokenIconUrl}></CoinImg>
        <CoinName>{name}</CoinName>
      </CoinItemLeft>
      <div>
        +{FaucetTokenSize[name!] ?? 0} {name}
      </div>
      {renderLayer1Actions()}
    </CoinItem>
  )
})
